import React from 'react'
import styled from '@emotion/styled'
import { Icon as IconV2 } from '@mobi/component-library/Assets/Icon'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { hexColors } from '@mobi/settings/hexColors'
import { Currency } from '@mobi/utils/money'
import { toggleIsDropDownOpen } from '@core/Areas/AppHeader/Store'
import { openDepositModal } from '@core/Areas/Deposit/openDeposit'
import { Money } from '@core/Components/Text/Money'
import { trackBonusBetEvent } from '@core/Services/Analytics/helpers/trackers'
import { keys } from '@core/Services/Analytics/keys'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { useAppDispatch, useAppSelector } from '@core/Store/hooks'
import { useRefreshBalanceOnForeground } from '@core/Utils/hooks'
import { useObservableImmutable } from '@core/Utils/hooks/useObservableImmutable'
import { getIsBalanceVisible, getIsDropDownOpen } from '../../Store/selectors'
import { FobSpecialToken } from '../Wallet/Components/helpers/useGetSpecialTokens'
import { useGetSpecialTokens } from '../Wallet/Components/helpers/useGetSpecialTokens'

type RenderContext = 'app-header' | 'betslip'

const enum LocalConstants {
  betslipHeaderClassname = 'js-balance-pill--betslip',
}

export const BalancePill: React.FC<{ renderContext: RenderContext }> = ({ renderContext }) => {
  const dispatch = useAppDispatch()

  const {
    accountNumber,
    accountBalance: balance = 0,
    bonusBetBalance = 0,
    bonusCashBalance = 0,
    campaigns,
  } = useObservableImmutable(userAccountState$, [
    'accountNumber',
    'accountBalance',
    'accountBalanceUpdatedTime',
    'bonusBetBalance',
    'bonusCashBalance',
    'campaigns',
  ])

  useRefreshBalanceOnForeground()

  const isDropDownOpen = useAppSelector(getIsDropDownOpen)
  const isBalanceVisible = useAppSelector(getIsBalanceVisible)

  const superPicksLastUpdated = React.useRef<number | null>(null)
  const standardSuperPickCount = React.useRef(0)
  const bonusSuperPickCount = React.useRef(0)
  const [canShowBonusCount, setCanShowBonusCount] = React.useState(false)
  const {
    tokens,
    errorMessage,
    isLoading: isLoadingSuperPicks,
  } = useGetSpecialTokens(canShowBonusCount)

  React.useEffect(() => {
    const bonusCountTimeoutId = window.setTimeout(() => {
      setCanShowBonusCount(true)
    }, 500)
    return () => {
      window.clearTimeout(bonusCountTimeoutId)
    }
  }, [])

  const handleDropDownToggle = () => {
    if (!isDropDownOpen && accountNumber) {
      trackBonusBetEvent(keys.accountDropDownOpened, {
        accountNumber,
        accountBalance: balance,
        bonusBetBalance,
        bonusCashBalance,
      })
    }
    dispatch(toggleIsDropDownOpen())
  }

  const bonusBalance = (bonusBetBalance ?? 0) + (bonusCashBalance ?? 0)
  const campaignsCount = campaigns?.count() || 0

  const standardSuperPicks = tokens.filter(token => !token.IsBonusToken)
  const bonusSuperPicks = tokens.filter(token => token.IsBonusToken)

  const isStandardSuperPickAvailable =
    !errorMessage && standardSuperPicks && standardSuperPicks.length > 0
  const isBonusSuperPicksAvailable = !errorMessage && bonusSuperPicks && bonusSuperPicks.length > 0

  if (!isLoadingSuperPicks) {
    standardSuperPickCount.current = isStandardSuperPickAvailable
      ? standardSuperPicks[0].BetsAvailable
      : 0
    bonusSuperPickCount.current = isBonusSuperPicksAvailable
      ? calculateTotalBonusSuperPicks(bonusSuperPicks)
      : 0
  }

  const totalOffersCount =
    campaignsCount + standardSuperPickCount.current + bonusSuperPickCount.current
  superPicksLastUpdated.current = Date.now()

  return (
    <BalancePillComponent
      renderContext={renderContext}
      cashBalance={balance || 0}
      bonusBalance={bonusBalance}
      bonusesCount={totalOffersCount}
      isVisible={isBalanceVisible}
      onWalletToggle={handleDropDownToggle}
    />
  )
}

// ===============
// Local Component
// ===============

// NOT main export, see above for <BalancePill />
export const BalancePillComponent: React.FC<{
  cashBalance: number
  bonusBalance: number
  bonusesCount: number
  isVisible: boolean
  onWalletToggle: () => void
  renderContext: RenderContext
}> = ({ cashBalance, bonusBalance, bonusesCount, isVisible, onWalletToggle, renderContext }) => {
  const hideDecimalsInCashBalance = cashBalance >= 1000
  const hideDecimalsInBonusBalance = bonusBalance >= 1000

  const balanceToDisplay = hideDecimalsInCashBalance ? Math.floor(cashBalance) : cashBalance
  const bonusBalanceToDisplay = hideDecimalsInBonusBalance ? Math.floor(bonusBalance) : bonusBalance

  const handleDepositNavFromBetSlip = () => {
    openDepositModal('quick-deposit')
  }

  return (
    <BalanceContainerStyled
      onClick={renderContext === 'app-header' ? onWalletToggle : handleDepositNavFromBetSlip}
      className={renderContext === 'betslip' ? LocalConstants.betslipHeaderClassname : ''}
      data-testid='app-header-balance'
    >
      {renderContext === 'app-header' && (
        <BalanceSectionDefaultAppHeaderStyled>
          {isVisible ? (
            <>
              <div>
                <BalanceMoneyStyled
                  amount={balanceToDisplay as number}
                  decimalPlaces={hideDecimalsInCashBalance ? 0 : 2}
                />

                {!!bonusBalanceToDisplay && (
                  <BonusBalanceMoneyWrapperStyled>
                    <IconV2
                      name='DuocolorCurrencyDollarCircle'
                      size='1.2rem'
                      color={colors.success[500]}
                    />

                    <BonusBalanceMoneyStyled>
                      {new Currency(bonusBalanceToDisplay).format({ pattern: '#' })}
                    </BonusBalanceMoneyStyled>
                  </BonusBalanceMoneyWrapperStyled>
                )}
              </div>

              {!!bonusesCount && <BonusSectionStyled>{bonusesCount}</BonusSectionStyled>}
            </>
          ) : (
            <Icon type='personMinimal' size='2.5rem' data-testid='user-icon' />
          )}
        </BalanceSectionDefaultAppHeaderStyled>
      )}

      {renderContext !== 'app-header' && (
        <BalanceSectionForBetSlipStyled>
          <div>
            <BalanceMoneyStyled
              amount={balanceToDisplay as number}
              decimalPlaces={hideDecimalsInCashBalance ? 0 : 2}
            />

            {!!bonusBalanceToDisplay && (
              <BonusBalanceMoneyWrapperStyled>
                <IconV2
                  name='DuocolorCurrencyDollarCircle'
                  size='1.2rem'
                  color={colors.success[500]}
                />

                <BonusBalanceMoneyStyled>
                  {new Currency(bonusBalanceToDisplay).format({ pattern: '#' })}
                </BonusBalanceMoneyStyled>
              </BonusBalanceMoneyWrapperStyled>
            )}
          </div>

          <IconV2 name='LinePlusCircle' size='1.6rem' />
        </BalanceSectionForBetSlipStyled>
      )}
    </BalanceContainerStyled>
  )
}

// =============
// Local Helpers
// =============

function calculateTotalBonusSuperPicks(array: FobSpecialToken[]): number {
  return array.reduce((count, { BetsAvailable = 0 }) => BetsAvailable + count, 0)
}

// ======
// Styles
// ======

const BalanceContainerStyled = styled.div({
  display: 'flex',
  alignItems: 'stretch',
  cursor: 'pointer',
})

const BalanceSectionDefaultAppHeaderStyled = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: spacing.smx2,

  backgroundColor: colors.studio[600],
  color: colors.white,
  borderRadius: radius.lg,
  padding: `${spacing.smx2} ${spacing.smx1}`,
  fontFamily: font.family.primary,
  fontWeight: font.weight.medium,
  fontSize: font.size.md.fontSize,
  lineHeight: font.size.md.lineHeight,
})

const BalanceSectionForBetSlipStyled = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: spacing.smx2,
  height: '4rem',

  backgroundColor: colors.surface[200],
  color: colors.neutral[900],
  padding: `0 ${spacing.smx1}`,
  border: 0,
  borderRadius: radius.md,

  fontFamily: font.family.primary,
  fontSize: font.size.md.fontSize,
  fontWeight: font.weight.medium,
  lineHeight: font.size.md.lineHeight,
})

const BonusSectionStyled = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2.2rem',
  height: '2.2rem',
  fontFamily: font.family.primary,
  fontWeight: font.weight.semibold,
  fontSize: font.size.md.fontSize,
  letterSpacing: font.size.md.letterSpacing,
  lineHeight: font.size.md.lineHeight,

  borderRadius: radius.full,
  backgroundColor: colors.orange[500],
  color: hexColors.darkOrange,
})

const BalanceMoneyStyled = styled
  .div({
    margin: 'unset',
    display: 'block',
  })
  .withComponent(Money)

const BonusBalanceMoneyWrapperStyled = styled.span({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '.2rem',

  background: colors.success[50],
  borderRadius: radius.sm,
  padding: '0 .2rem',
})

const BonusBalanceMoneyStyled = styled.span({
  color: colors.celery[500],
  margin: 'unset',
  display: 'block',
  whiteSpace: 'nowrap',
})
